import { useState, useEffect, useMemo, useId } from "react";
import { Search, ChevronRight } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import api from "../api.js";
import LoadError from "./LoadError.jsx";
import { useAuth } from "../contexts/AuthContext.jsx";

// ── Searchable checkbox list ──────────────────────────────────────────────────
function CheckList({ items, selected, onToggle, placeholder = "Поиск...", emptyLabel = "Список не настроен", onConfigure, showSearch = true }) {
  const [q, setQ] = useState("");
  const listId = useId();
  const filtered = useMemo(() => {
    const lq = q.toLowerCase();
    return lq ? items.filter(i => i.name.toLowerCase().includes(lq)) : items;
  }, [items, q]);

  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 py-4 px-3 rounded-xl bg-slate-50 border border-dashed border-slate-200 text-center">
        <p className="text-[11px] text-slate-400 leading-snug">{emptyLabel}</p>
        {onConfigure && (
          <button onClick={onConfigure} className="text-[11px] font-semibold text-blue-500 hover:text-blue-700 transition-colors">
            Настроить список →
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      {showSearch && items.length > 5 && (
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            className="pl-8 h-7 text-xs"
            placeholder={placeholder}
            value={q}
            onChange={e => setQ(e.target.value)}
          />
        </div>
      )}
      <div className="flex flex-col gap-0.5 mt-0.5">
        {filtered.length === 0 && (
          <p className="text-[11px] text-slate-400 py-2 text-center">Ничего не найдено</p>
        )}
        {filtered.map(item => (
          <div key={item.id} className="flex items-center gap-2 py-0.5 group">
            <Checkbox
              id={`${listId}-${item.id}`}
              checked={selected.includes(item.name)}
              onCheckedChange={() => onToggle(item.name)}
              className="h-3.5 w-3.5"
            />
            <Label htmlFor={`${listId}-${item.id}`}
              className="flex-1 text-xs font-normal leading-tight cursor-pointer text-slate-700 group-hover:text-slate-900">
              {item.name}
            </Label>
          </div>
        ))}
      </div>
    </div>
  );
}


// ── Section label ─────────────────────────────────────────────────────────────
function SectionLabel({ children }) {
  return (
    <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">{children}</p>
  );
}

// ── Read-only badge list — old-format scans (complaintsFormat 1) get shown as plain badges
// instead of the (now-retired) editable pickers, since they don't carry the new symptom/
// footType/cornZone categories at all.
function ReadOnlyList({ label, items }) {
  if (!items?.length) return null;
  return (
    <div className="mb-2">
      <p className="text-[10px] font-semibold text-slate-500 mb-1">{label}</p>
      <div className="flex flex-wrap gap-1">
        {items.map(name => (
          <span key={name} className="inline-block px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full text-[10px] font-medium">{name}</span>
        ))}
      </div>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function ScanParamsPanel({ params, onChange, onNavigateToPatient, onNavigate: navigate, selectedPatient, readOnly = false }) {
  const { user } = useAuth();
  const onNavigate = user?.role === "admin" ? navigate : undefined;
  const [symptomTypes, setSymptomTypes]   = useState([]);
  const [footTypeTypes, setFootTypeTypes] = useState([]);
  const [cornZoneTypes, setCornZoneTypes] = useState([]);
  const [generalSymptomTypes, setGeneralSymptomTypes] = useState([]);
  const [shoeTypes, setShoeTypes]           = useState([]);
  const [loadTypes, setLoadTypes]           = useState([]);
  const [catalogAttempt, setCatalogAttempt] = useState(0);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [catalogError, setCatalogError] = useState("");

  useEffect(() => {
    let active = true;
    setCatalogLoading(true);
    const catalogs = [
      ["симптомы", api.listComplaintTypes, setSymptomTypes],
      ["типы стопы", api.listFootTypes, setFootTypeTypes],
      ["зоны натоптышей", api.listCornZones, setCornZoneTypes],
      ["общие жалобы", api.listGeneralSymptoms, setGeneralSymptomTypes],
      ["обувь", api.listShoeTypes, setShoeTypes],
      ["нагрузка", api.listLoadTypes, setLoadTypes],
    ];
    Promise.allSettled(catalogs.map(async ([, load, setItems]) => {
      const items = await load();
      if (active) setItems(items);
    })).then(results => {
      if (!active) return;
      const failed = results.flatMap((result, index) => result.status === "rejected" ? [catalogs[index][0]] : []);
      setCatalogError(failed.length ? `Не удалось загрузить списки: ${failed.join(", ")}. Повторите загрузку.` : "");
      setCatalogLoading(false);
    });
    return () => { active = false; };
  }, [catalogAttempt]);

  const toggleFootField = (foot, field, name) => {
    const cur = params[foot]?.[field] || [];
    onChange({ ...params, [foot]: { ...params[foot], [field]: cur.includes(name) ? cur.filter(v => v !== name) : [...cur, name] } });
  };

  const toggleGeneralSymptom = (name) => {
    const cur = params.generalSymptoms || [];
    onChange({ ...params, generalSymptoms: cur.includes(name) ? cur.filter(s => s !== name) : [...cur, name] });
  };

  const toggleShoeType = (name) => {
    const cur = params.shoeTypes || [];
    onChange({ ...params, shoeTypes: cur.includes(name) ? cur.filter(s => s !== name) : [...cur, name] });
  };

  return (
    <div className="flex flex-col bg-white overflow-hidden flex-1 min-h-0" aria-busy={catalogLoading}>
      {catalogError && <div className="px-3 py-2 shrink-0">
        <LoadError message={catalogError} onRetry={() => setCatalogAttempt(attempt => attempt + 1)} retrying={catalogLoading} />
      </div>}

      {/* Patient chip */}
      {selectedPatient && (
        <div className="flex items-center justify-between px-4 py-2 bg-primary/5 border-b border-primary/20 flex-shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
              <span className="text-[10px] font-bold text-primary">
                {selectedPatient.name?.[0]?.toUpperCase()}
              </span>
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-slate-800 truncate" title={selectedPatient.alias || selectedPatient.name}>{selectedPatient.alias || selectedPatient.name}</p>
              <p className="text-[11px] text-slate-500 truncate">
                {selectedPatient.birthDate ? new Date(`${selectedPatient.birthDate.slice(0, 10)}T12:00:00`).toLocaleDateString("ru-RU") : "Дата рождения не указана"}
                {selectedPatient.alias && selectedPatient.alias !== selectedPatient.name ? ` · В карточке: ${selectedPatient.name}` : ""}
              </p>
            </div>
          </div>
          {onNavigateToPatient && (
            <button
              onClick={() => onNavigateToPatient(selectedPatient.id)}
              className="flex items-center gap-0.5 text-[11px] text-primary hover:underline font-medium flex-shrink-0 ml-2"
            >
              Карточка <ChevronRight className="h-3 w-3" />
            </button>
          )}
        </div>
      )}

      {/* Tabs */}
      <Tabs defaultValue="complaints" className="flex flex-col flex-1 min-h-0">
        <TabsList className="w-full rounded-none bg-white border-b border-slate-200 h-10 p-0 gap-0 justify-start flex-shrink-0">
          {[
            { value: "complaints", label: "Жалобы" },
            { value: "shoes",      label: "Обувь и нагрузка" },
            { value: "conclusion", label: "Заключение" },
          ].map(t => (
            <TabsTrigger
              key={t.value}
              value={t.value}
              className="flex-1 rounded-none h-full text-[11.5px] font-semibold data-[state=active]:bg-blue-50 data-[state=active]:text-blue-600 data-[state=active]:shadow-none text-slate-500 transition-all"
            >
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* ── Жалобы ── */}
        <TabsContent value="complaints" className="flex-1 mt-0 min-h-0">
          <ScrollArea className="h-full">
            <div className="p-4 flex flex-col gap-4">
              {readOnly ? (
                <>
                  <div className="mb-1 px-2.5 py-1.5 rounded-lg bg-amber-50 border border-amber-200 text-[11px] text-amber-700">
                    Старый формат обследования — только просмотр
                  </div>
                  {["left", "right"].map(side => (
                    <div key={side}>
                      <div className="flex items-center gap-2 mb-2">
                        <span className={cn("w-2 h-2 rounded-full", side === "left" ? "bg-blue-500" : "bg-green-500")} />
                        <SectionLabel>{side === "left" ? "Левая стопа" : "Правая стопа"}</SectionLabel>
                      </div>
                      <ReadOnlyList label="Заболевания" items={params[side]?.diseases} />
                      <ReadOnlyList label="Жалобы" items={params[side]?.complaints} />
                      {params[side]?.complaintsText && (
                        <div className="mb-2">
                          <p className="text-[10px] font-semibold text-slate-500 mb-1">Дополнительные жалобы</p>
                          <p className="text-xs text-slate-600 whitespace-pre-wrap">{params[side].complaintsText}</p>
                        </div>
                      )}
                      {params[side]?.otherText && (
                        <div className="mb-2">
                          <p className="text-[10px] font-semibold text-slate-500 mb-1">Другое заболевание</p>
                          <p className="text-xs text-slate-600 whitespace-pre-wrap">{params[side].otherText}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </>
              ) : (
                <>
                  <div>
                    <SectionLabel>Общие симптомы</SectionLabel>
                    <CheckList
                      items={generalSymptomTypes}
                      selected={params.generalSymptoms || []}
                      onToggle={toggleGeneralSymptom}
                      emptyLabel="Список не настроен"
                      onConfigure={onNavigate ? () => onNavigate("fieldconfig") : undefined}
                      showSearch={false}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    {["left", "right"].map(side => (
                      <div key={side}>
                        <div className="flex items-center gap-2 mb-2">
                          <span className={cn("w-2 h-2 rounded-full", side === "left" ? "bg-blue-500" : "bg-green-500")} />
                          <SectionLabel>{side === "left" ? "Левая стопа" : "Правая стопа"}</SectionLabel>
                        </div>

                        <p className="text-[10px] font-semibold text-slate-500 mb-1">Симптомы</p>
                        <CheckList
                          items={symptomTypes}
                          selected={(params[side]?.symptoms) || []}
                          onToggle={name => toggleFootField(side, "symptoms", name)}
                          emptyLabel="Список не настроен"
                          onConfigure={onNavigate ? () => onNavigate("fieldconfig") : undefined}
                          showSearch={false}
                        />

                        <p className="text-[10px] font-semibold text-slate-500 mt-3 mb-1">Тип стопы</p>
                        <CheckList
                          items={footTypeTypes}
                          selected={(params[side]?.footTypes) || []}
                          onToggle={name => toggleFootField(side, "footTypes", name)}
                          emptyLabel="Список не настроен"
                          onConfigure={onNavigate ? () => onNavigate("fieldconfig") : undefined}
                          showSearch={false}
                        />

                        <p className="text-[10px] font-semibold text-slate-500 mt-3 mb-1">Натоптыши по зонам</p>
                        <CheckList
                          items={cornZoneTypes}
                          selected={(params[side]?.cornZones) || []}
                          onToggle={name => toggleFootField(side, "cornZones", name)}
                          emptyLabel="Список не настроен"
                          onConfigure={onNavigate ? () => onNavigate("fieldconfig") : undefined}
                          showSearch={false}
                        />
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </ScrollArea>
        </TabsContent>

        {/* ── Обувь и нагрузка ── */}
        <TabsContent value="shoes" className="flex-1 mt-0 min-h-0">
          <ScrollArea className="h-full">
            <div className={cn("p-4 flex flex-col gap-3", readOnly && "pointer-events-none opacity-60")}>

              {/* Вес */}
              <div className="flex items-center gap-3">
                <div className="flex flex-col gap-1 flex-1">
                  <SectionLabel>Вес пациента</SectionLabel>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min="0"
                      max="300"
                      placeholder="кг"
                      value={params.weight || ""}
                      onChange={e => onChange({ ...params, weight: e.target.value })}
                      className="w-20 h-7 text-xs"
                    />
                    <span className="text-xs text-slate-400">кг</span>
                  </div>
                </div>
              </div>

              {/* Тип обуви */}
              <div>
                <SectionLabel>Тип обуви</SectionLabel>
                <CheckList
                  items={shoeTypes}
                  selected={params.shoeTypes || []}
                  onToggle={toggleShoeType}
                  placeholder="Поиск типа..."
                  emptyLabel="Типы обуви не настроены"
                  onConfigure={onNavigate ? () => onNavigate("fieldconfig") : undefined}
                />
              </div>

              {/* Тип нагрузки */}
              <div>
                <SectionLabel>Тип нагрузки</SectionLabel>
                {loadTypes.length === 0 ? (
                  <div className="flex flex-col items-center gap-2 py-3 px-3 rounded-xl bg-slate-50 border border-dashed border-slate-200 text-center">
                    <p className="text-[11px] text-slate-400">Типы нагрузки не настроены</p>
                    {onNavigate && (
                      <button onClick={() => onNavigate("fieldconfig")} className="text-[11px] font-semibold text-blue-500 hover:text-blue-700 transition-colors">
                        Настроить →
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-col gap-0.5">
                    {/* Пустой вариант */}
                    <div
                      className={`flex items-center gap-2 py-1 px-2 rounded-lg cursor-pointer text-xs transition-colors ${!params.loadTypeId ? "bg-blue-50 text-blue-700 font-semibold" : "text-slate-500 hover:bg-slate-50"}`}
                      onClick={() => onChange({ ...params, loadTypeId: "" })}
                    >
                      {!params.loadTypeId && <span className="text-blue-500">✓</span>}
                      <span>— Не указано</span>
                    </div>
                    {loadTypes.map(lt => (
                      <div
                        key={lt.id}
                        className={`flex items-center gap-2 py-1 px-2 rounded-lg cursor-pointer text-xs transition-colors ${params.loadTypeId === lt.id ? "bg-blue-50 text-blue-700 font-semibold" : "text-slate-600 hover:bg-slate-50"}`}
                        onClick={() => onChange({ ...params, loadTypeId: lt.id })}
                      >
                        {params.loadTypeId === lt.id && <span className="text-blue-500">✓</span>}
                        <span>{lt.name}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

            </div>
          </ScrollArea>
        </TabsContent>

        <TabsContent value="conclusion" className="flex-1 mt-0 min-h-0">
          <ScrollArea className="h-full">
            <div className={cn("p-4 flex flex-col gap-3", readOnly && "pointer-events-none opacity-60")}>
              <p className="text-xs text-slate-500">Рекомендации и комментарий попадут в отчёт пациента.</p>
              {/* Рекомендации */}
              <div>
                <SectionLabel>Рекомендации</SectionLabel>
                <Textarea
                  placeholder="Заключение и рекомендации..."
                  value={params.noteRecommendations || ""}
                  onChange={e => onChange({ ...params, noteRecommendations: e.target.value })}
                  className="text-xs min-h-[64px] resize-none"
                />
              </div>

              {/* Комментарий */}
              <div>
                <SectionLabel>Комментарий</SectionLabel>
                <Textarea
                  placeholder="Дополнительные наблюдения..."
                  value={params.noteComment || ""}
                  onChange={e => onChange({ ...params, noteComment: e.target.value })}
                  className="text-xs min-h-[52px] resize-none"
                />
              </div>

              {/* Скрытый комментарий — виден только персоналу, никогда не печатается в отчёте */}
              <div>
                <SectionLabel>Скрытая заметка (не в отчёт)</SectionLabel>
                <Textarea
                  placeholder="Заметка для персонала, в отчёт не попадёт..."
                  value={params.internalNote || ""}
                  onChange={e => onChange({ ...params, internalNote: e.target.value })}
                  className="text-xs min-h-[52px] resize-none"
                />
              </div>

            </div>
          </ScrollArea>
        </TabsContent>
      </Tabs>

    </div>
  );
}
